import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getUserStatusVariant } from "@/lib/labels";
import { useUserDetail } from "@/hooks/use-user-detail";
import { useAccountDetail } from "@/hooks/use-account-detail";
import { UserProfileTab } from "@/components/users/user-profile-tab";
import { UserRequestsTab } from "@/components/users/user-requests-tab";
import { UserCasesTab } from "@/components/users/user-cases-tab";
import { UserVehiclesTab } from "@/components/users/user-vehicles-tab";
import { useRouter } from "@/i18n/navigation";

const USER_TABS = ["profile", "requests", "cases", "vehicles"] as const;

function isUserTab(value: unknown): value is (typeof USER_TABS)[number] {
  return typeof value === "string" && USER_TABS.includes(value as (typeof USER_TABS)[number]);
}

function UserDetailSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      <div className="flex items-center gap-4">
        <Skeleton className="size-16 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </div>
      <Skeleton className="h-10 w-80" />
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-lg border p-6 space-y-4">
            <Skeleton className="h-5 w-40" />
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-40" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_dashboard/users/$id")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: isUserTab(search.tab) && search.tab !== "profile" ? search.tab : undefined,
  }),
  component: UserDetailPage,
});

function UserDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const activeTab = tab ?? "profile";
  const t = useTranslations("UserDetail");
  const tStatus = useTranslations("UserStatus");
  const tRole = useTranslations("RoleLabels");
  const { data: detail, isLoading, isFetching, isError, refetch } = useUserDetail(id);
  const { data: account } = useAccountDetail();
  const router = useRouter();
  const isAdmin = account?.roles.includes("admin") ?? false;

  function handleTabChange(value: string) {
    const query = value === "profile" ? "" : `?tab=${value}`;
    router.replace(`/users/${id}${query}`, { scroll: false });
  }

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="..."
          parent={{ label: t("breadcrumbParent"), href: "/users" }}
        />
        <UserDetailSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader title="—" parent={{ label: t("breadcrumbParent"), href: "/users" }} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
          <p className="text-muted-foreground">{t("loadError")}</p>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? t("retrying") : t("retry")}
          </Button>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader
          title="—"
          parent={{ label: t("breadcrumbParent"), href: "/users" }}
        />
        <div className="flex flex-1 items-center justify-center p-4">
          <p className="text-muted-foreground">{t("notFound")}</p>
        </div>
      </>
    );
  }

  const { user, firstName, lastName, roles, avatarUrl } = detail;
  const firstNameStr = firstName ?? "";
  const lastNameStr = lastName ?? "";
  const initials = `${firstNameStr[0] ?? ""}${lastNameStr[0] ?? ""}`;
  const fullName = `${firstNameStr} ${lastNameStr}`.trim();
  const displayName = fullName || t("title");

  return (
    <>
      <PageHeader
        title={displayName}
        parent={{ label: t("breadcrumbParent"), href: "/users" }}
      />
      <div className="flex flex-1 flex-col gap-6 p-4">
        {/* Header section — always visible */}
        <div className="flex items-center gap-4">
          <Avatar size="lg">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback>{initials || "?"}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold">{displayName}</h1>
            <div className="flex flex-wrap gap-2">
              <Badge variant={getUserStatusVariant(user.status)}>
                {tStatus(user.status as "active")}
              </Badge>
              {roles.map((role) => (
                <Badge key={role} variant="outline">
                  {tRole.has(role as "admin") ? tRole(role as "admin") : role}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="profile">{t("tabProfile")}</TabsTrigger>
            <TabsTrigger value="requests">{t("tabRequests")}</TabsTrigger>
            <TabsTrigger value="cases">{t("tabCases")}</TabsTrigger>
            <TabsTrigger value="vehicles">{t("tabVehicles")}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <UserProfileTab detail={detail} isAdmin={isAdmin} onUpdated={refetch} />
          </TabsContent>

          <TabsContent value="requests">
            <UserRequestsTab userId={user.id} emptyMessage={t("noRequests")} />
          </TabsContent>

          <TabsContent value="cases">
            <UserCasesTab userId={user.id} emptyMessage={t("noCases")} />
          </TabsContent>

          <TabsContent value="vehicles">
            <UserVehiclesTab userId={user.id} emptyMessage={t("noVehicles")} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
